import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'

import { instance } from '@/api/instances'

const PAGINATION_LIMIT = 10

// share with health events store
const getNewRange = ({ currRange, loadPrev }) => {
  let newRange = []
  if (!currRange) {
    newRange = [0, PAGINATION_LIMIT - 1]
  } else {
    const init = loadPrev ? currRange[0] - PAGINATION_LIMIT : currRange[1] + 1
    const ends = loadPrev
      ? currRange[1] - PAGINATION_LIMIT
      : currRange[1] + PAGINATION_LIMIT

    newRange = [init, ends]
  }

  return newRange
}

const clearHealthEvents = store => {
  store.commit('clearHealthEvents')
}

const fetchEvents = async (store, { clearHealthEvents = false } = {}) => {
  store.commit('setPaginationError', false)
  store.commit('setIsFetching', true)

  try {
    if (clearHealthEvents) store.commit('clearHealthEvents')

    const filters = mapKeys(store.state.filters, (_, key) => snakeCase(key))
    const sortBy = snakeCase(store.state.sortBy)

    const newRange = getNewRange({
      currRange: store.state.pagination.range,
      loadPrev: false,
      PAGINATION_LIMIT
    })
    store.commit('setRange', newRange)

    const data = {
      filters,
      sortBy,
      sortOrder: store.state.sortOrder,
      range: newRange
    }
    const healthEvents = await store.dispatch(
      'healthEvents/getPersonHealthEvents',
      data,
      { root: true }
    )

    if (healthEvents.length < PAGINATION_LIMIT) {
      store.commit('setStopLoad', true)
    }

    store.commit('setHealthEvents', healthEvents)
  } catch (error) {
    const undoRangeIncrease = () => {
      const newRange = getNewRange({
        currRange: store.state.pagination.range,
        loadPrev: true,
        PAGINATION_LIMIT
      })
      store.commit('setRange', newRange)
    }

    undoRangeIncrease()
    store.commit('setPaginationError', true)

    store.dispatch(
      'errors/setError',
      { error, request: 'cockpit/fetchEvents' },
      { root: true }
    )
  } finally {
    store.commit('setIsFetching', false)
  }
}

const fetchFilters = async store => {
  try {
    const { summaries, pool } = await instance.get(`staff/cockpit`)
    store.commit('setSummaries', summaries)
    store.commit('setPoolFilters', pool)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'cockpit/fetchFilters' },
      { root: true }
    )
  }
}

const registerEHRSpecialistAccess = async (store, data) => {
  try {
    const id = await instance.post('handleAccessReason', {
      json: data
    })

    return id
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'cockpit/registerEHRSpecialistAccess' },
      { root: true }
    )
    return null
  }
}

const fetchSecondaryFilters = async (store, data) => {
  try {
    const { filter } = Object.fromEntries(new URLSearchParams(data.query))
    const filters = JSON.parse(filter)
    const sortBy = snakeCase(store.rootState.healthEvents.sortBy)
    const sortOrder = snakeCase(store.state.sortOrder)
    const healthEvents = await store.dispatch(
      'healthEvents/getPersonHealthEvents',
      {
        filters,
        sortBy,
        sortOrder,
        range: store.rootState.healthEvents.range
      },
      {
        root: true
      }
    )

    store.commit('setCurrentSecondaryFilters', data)
    return healthEvents
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'cockpit/setCurrentSecondaryFilters' },
      { root: true }
    )
    return null
  }
}
const setCurrentSecondaryFilters = (store, data) => {
  store.commit('setCurrentSecondaryFilters', data)
}

const resetPagination = store => {
  store.commit('setRange', null)
  store.commit('setStopLoad', false)
}

const updateHealthEventLocally = (store, data) => {
  store.commit('updateHealthEventLocally', data)
}

const removeHealthEventLocally = (store, id) => {
  store.commit('removeHealthEventLocally', id)
}

const sanitizeFilters = (store, { filters }) => {
  store.commit('sanitizeFilters', { filters })
}

const resetCockpit = store => {
  clearHealthEvents(store)
  resetPagination(store)
}

export default {
  fetchEvents,
  fetchFilters,
  registerEHRSpecialistAccess,
  fetchSecondaryFilters,
  setCurrentSecondaryFilters,
  resetPagination,
  updateHealthEventLocally,
  removeHealthEventLocally,
  sanitizeFilters,
  clearHealthEvents,
  resetCockpit
}
